type StringDict = Record<string, string>
type StringDictExt = Record<string, string|string[]>

type BuilderStrings = {
  title: StringDict
  sectionTitle: StringDict
  copy: StringDict
  action: StringDict
  affirmation: StringDictExt
  option: StringDict
  question: StringDict
  answer: StringDict
  sharingTitle: StringDict
  sharingDetail: StringDict
  license: StringDict
  json: StringDict
}

const title: StringDict = {
  generatedDate: `Date of license generation`,
  expiryDate: `Expiry date`,
  expiryActions: `Actions on expiry`,
  noExpiryStatement: `No expiry`,
  datasetTitle: `Dataset title`,
  datasetDescription: `Dataset description`,
  yearGenerated: `Year of dataset creation`,
  identifiersList: `Dataset identifiers and links`,
  datasetDetailsPrompt: `Dataset details`,
  accessModePrompt: `Type of sharing permitted`,
  commercialUsePrompt: `Commercial use`,
  onwardSharingPrompt: `Onward sharing`,
  shareAlikePrompt: `Share-alike`,
  modificationsPrompt: `Modifications`,
  benefitSharingPrompt: `Benefit sharing`,
  costRecoveryPrompt: `Cost recovery`,
  benefitSharingContactEmail: `Contact email`,
  benefitSharingContactUrl: `Information link`,
  benefitSharingActivities: `Expected benefit-sharing activities`,
  costRecoveryContactEmail: `Contact email`,
  costRecoveryContactUrl: `Information link`,
  costRecoveryDetails: `Cost-recovery details`,
  publicationRequirementsPrompt: `Publication & dissemination requirements`,
  attributionRequiredPrompt: `Attribution`,
  attributionWording: `Attribution wording`,
  authorshipRequiredPrompt: `Authorship`,
  accessPathway: `Recipient access pathway`,
  embargoEndDate: `Embargo end date`,
  embargoReason: `Reason for embargo`,
  contactName: `Primary contact name`,
  contactEmail: `Primary contact email`,
  dataCustodianName: `Data custodian name`,
  dataCustodianEmail: `Data custodian email`,
  affiliationName: `Affiliation name`,
  affiliationCountry: `Affiliation host country`,
  piName: `Principal investigator name`,
  piEmail: `Principal investigator email`,
  dueDiligenceConfirmation: `Due diligence confirmation`,
  confidentialityDate: `Patient confidentiality date`,
  confidentialityConfirmation: `Patient confidentiality confirmation`,
  licenseIrrevocableStatement: `License irrevocability`,
  termsAcknowledgementPrompt: `Acknowledge license terms`,
  licenseExpiry: `License expiry`,
}

const sectionTitle = {
  datasetDetails: `Dataset Details`,
  licenseDuration: `License Duration`,
  sharing: 'Sharing',
  benefitSharing: `Benefit Sharing`,
  costRecovery: `Cost Recovery`,
}

const copy: StringDict = {
  builderIntro: `Set your license terms below. As you edit, the panel on the right updates in real time. \nUse it to preview and download both human-readable and machine-readable versions`,
}

const action: StringDict = {
  addIdentifier: `Add dataset identifier or link`,
  removeIdentifier: `Remove identifier`,
}

const affirmation: StringDictExt = {
  expiryDate: `There is an expiry date for this license.`,
  commercialUse: [`The data may be used for the development of commercial products.`, `The data may not be used for the development of commercial products.`],
  onwardSharing: [`Onward sharing with others is allowed.`, `Onward sharing with others is not allowed.`],
  shareAlike: [
    `Share-alike is required, and any onward sharing must be done under the original terms of sharing as defined in this license.`, 
    `Share-alike is not required, and any onward sharing can be done under different conditions to this license if preferred.`
  ],
  modifications: ['Sharing of dataset derivatives and modified forms of the dataset is permitted.', 'Sharing of dataset derivatives and modified forms of the dataset is not permitted.'],
  benefitSharing: [`A benefit-sharing agreement is required.`, `No benefit-sharing is required.`],
  costRecovery: [`There is a cost recovery payment required for using this dataset`, `There is no cost recovery payment required for using this dataset`]
}

const option: StringDict = {
  yes: `Yes`,
  no: `No`,
}

const question: StringDict = {
  expiryDate: `Is there an expiry date for this license?`,
  expiryActions: `What actions must be taken on expiry of this license? (e.g., confirmation of deletion of dataset, or adoption of a new license)`,
  datasetTitle: `What is the name or title of your dataset`,
  datasetDescription: `Dataset description`,
  yearGenerated: `In what year was this dataset generated?`,
  identifiersList: `Does this dataset have identifiers and/or links that you wish to list on the license?`,
  datasetDetailsPrompt: `Do you want to add dataset details?`,
  accessModePrompt: `What type of sharing is permitted? Multiple selections are possible.`,
  commercialUsePrompt: `Can the data be used for the development of commercial products and outputs?`,
  onwardSharingPrompt: `May the dataset recipient share it onward to others?`,
  shareAlikePrompt: `Is share-alike required, where any onward sharing must have the same terms as the original terms of sharing provided in this license?`,
  modificationsPrompt: `Is sharing of dataset derivatives and modified forms of the dataset permitted?`,
  benefitSharingPrompt: `Is benefit-sharing a requirement of using this dataset?`,
  costRecoveryPrompt: `Is there a cost-recovery charge associated with using this dataset?`,
  benefitSharingContactEmail: `Which email address should recipients use to discuss benefit-sharing arrangements?`,
  benefitSharingContactUrl: `Do you have a URL with further detail on benefit-sharing expectations? (optional)`,
  benefitSharingActivities: `Describe the benefit-sharing activities that dataset recipients are expected to undertake.`,
  costRecoveryContactEmail: `Which email address should recipients use to arrange cost-recovery payments?`,
  costRecoveryContactUrl: `Do you have a URL with further detail on cost-recovery arrangements? (optional)`,
  costRecoveryDetails: `Describe the cost-recovery payment or process that applies to recipients.`,
  publicationRequirementsPrompt: `What are the requirements for publishing and disseminating results from working with these data?`,
  attributionRequiredPrompt: `Is attribution required when these data are used?`,
  attributionWording: `If attribution is required, what wording or format should be used?`,
  authorshipRequiredPrompt: `Is authorship required for any published outputs using these data?`,
  accessPathway: `What is the access pathway for the recipient?`,
  embargoEndDate: `If there is an embargo, when does it end?`,
  embargoReason: `If there is an embargo, what is the reason?`,
  contactName: `Who is the primary point of contact (name)?`,
  contactEmail: `What is the primary point of contact’s email?`,
  dataCustodianName: `Who is the data custodian (name)?`,
  dataCustodianEmail: `What is the data custodian’s email?`,
  affiliationName: `What is the affiliation (name)?`,
  affiliationCountry: `What is the affiliation’s host country?`,
  piName: `Who is the principal investigator or group leader (name)?`,
  piEmail: `What is the principal investigator or group leader’s email?`,
  dueDiligenceConfirmation: `I confirm that due diligence has been performed for these data.`,
  confidentialityDate: `On what date does the patient confidentiality obligation end (if applicable)?`,
  confidentialityConfirmation: `I confirm adherence to patient confidentiality obligations, where applicable.`,
  termsAcknowledgementPrompt: `I have read and understand the terms of the Data Sharing License.`,
  licenseIrrevocableStatement: `I understand that once I release a dataset to a recipient under this license, I cannot revoke it or change the terms for that recipient.`,
}

const answer: StringDict = {
  generatedDate: `Date of Generation: $today`,
  expiryDate: `Date of Expiry: $expiryDate`,
  accessDirect: `Direct`,
  accessCollaborative: `Collaborative`,
  accessFederated: `Federated`,
  accessTRE: `TRE`,
  commercialUsePermitted: `Commercial use permitted`,
  commercialUseProhibited: `No Commercial`,
  onwardSharingProhibited: `No Onward Sharing`,
  shareAlikeRequired: `Sharealike`,
  modificationsProhibited: `No Modifications`,
  costRecoveryNotRequired: `No cost recovery payment`,
  costRecoveryRequired: `Cost recovery payment required`,
  publicationReviewRequired: `Review`,
  publicationAggregatedOnly: `Aggregated Data`,
  publicationProhibited: `No Publication`,
}

const sharingTitle: StringDict = {
  accessDirect: `Direct sharing`,
  accessCollaborative: `Collaborative sharing`,
  accessFederated: `Federated analysis`,
  accessTRE: `Trusted research environment`
}

const sharingDetail: StringDict = {
  accessDirect: `The data will be provided directly and in full to the recipient for their analysis.`,
  accessCollaborative: `The provider and recipient will work together to do a joint analysis on the data. `,
  accessFederated: `The provider and recipient will each do the same analysis on their own dataset, and the findings will be reported together. `,
  accessTRE: `The data will be made available on a secure platform for research analysis by authorised users. The data will be blocked from download and obscured from view, and only analysis results will be available for download.`
}


const license: StringDict = {
  generatedDate: `Date of Generation: $today`,
  expiryDate: `This Data Sharing License expires on: $expiryDate`,
  expiryActions: `On expiry, the following actions are required: $text`,
  noExpiryStatement: `This Data Sharing License does not expire.`,
  datasetTitle: `Dataset title: $datasetTitle`,
  datasetDescription: `Dataset description: $datasetDescription`,
  yearGenerated: `Year of dataset creation: $yearGenerated`,
  identifiersList: `Dataset identifiers/links: $identifiers (URLs: $urls)`,
  accessDirect: `Access mode: Direct`,
  accessCollaborative: `Access mode: Collaborative`,
  accessFederated: `Access mode: Federated`,
  accessTRE: `Access mode: TRE`,
  accessPathway: `Access pathway for recipient: $text`,
  commercialUsePermitted: `The data can be used for the development of commercial products and outputs.`,
  commercialUseProhibited: `The data cannot be used for the development of commercial products and outputs.`,
  onwardSharingProhibited: `Onward sharing by the recipient is not permitted.`,
  shareAlikeRequired: `Onward sharing is permitted only under the same terms (“share-alike”).`,
  modificationsProhibited: `Sharing of dataset derivatives and modified forms of the dataset is not permitted.`,
  benefitSharing: `Benefit-sharing requirement: $text`,
  benefitSharingContactEmail: `Benefit-sharing contact email: $text`,
  benefitSharingContactUrl: `Benefit-sharing information link: $url`,
  benefitSharingActivities: `Expected benefit-sharing activities: $text`,
  costRecoveryContactEmail: `Cost-recovery contact email: $text`,
  costRecoveryContactUrl: `Cost-recovery information link: $url`,
  costRecoveryDetails: `Cost-recovery details: $text`,
  costRecoveryRequired: `There is a requirement for a cost-recovery payment for use of this dataset. Details: $details`,
  publicationRequirementsPrompt: `Publication and dissemination requirements: $text`,
  publicationReviewRequired: `Publication or dissemination requires prior review.`,
  publicationAggregatedOnly: `Only aggregate (de-identified) data derived from these data may be reported or disseminated.`,
  publicationProhibited: `No publication or dissemination of results from working with these data is permitted.`,
  attributionRequiredPrompt: `Attribution is required when these data are used.`,
  attributionWording: `Required attribution wording: $text`,
  authorshipRequiredPrompt: `Authorship is required for any published outputs using these data.`,
  embargoEndDate: `End of embargo: $embargoEndDate`,
  embargoReason: `Reason for embargo: $reason`,
  contactName: `Primary point of contact: $text`,
  contactEmail: `Primary point of contact email: $text`,
  dataCustodianName: `Data custodian: $text`,
  dataCustodianEmail: `Data custodian email: $text`,
  affiliationName: `Affiliation: $text`,
  affiliationCountry: `Affiliation host country: $text`,
  piName: `Principal investigator / group leader: $text`,
  piEmail: `Principal investigator / group leader email: $text`,
  dueDiligenceConfirmation: `Due diligence confirmed.`,
  confidentialityDate: `Patient confidentiality obligation end date: $date`,
  confidentialityConfirmation: `Patient confidentiality obligations confirmed.`,
  licenseIrrevocableStatement: `When a dataset is shared with a recipient under this license, the license cannot be revoked or changed for that recipient.`,
  termsAcknowledgementPrompt: `The data provider has read and understands the terms of the Data Sharing License.`,
}

const json: StringDict = {
  generatedDate: `YYYYMMDD`,
  expiryDate: `YYYYMMDD`,
  yearGenerated: `YYYY`,
  identifiersList: `pipe-delimited`,
}

export const strings: BuilderStrings = {
  title,
  sectionTitle,
  copy,
  action,
  affirmation,
  option,
  question,
  answer,
  sharingTitle,
  sharingDetail,
  license,
  json
}

export type { BuilderStrings, StringDict }
